import {useMemo, useState} from 'react';
import {FACTION_CONFIGS, RANDOM_FACTION_CONFIG, RANDOM_FACTION_ID} from '../../configs/factions';
import {getFactionSkinDisplayProps} from '../../utils/factions/getFactionSkinDisplayProps';

const sortByAvailability = (configs, isAvailable) => {
  return [...configs].sort((a, b) => {
    const aAvailable = isAvailable(a);
    const bAvailable = isAvailable(b);
    if (aAvailable !== bAvailable) {
      return aAvailable ? -1 : 1;
    }

    const aImplemented = a.implemented;
    const bImplemented = b.implemented;
    if (aImplemented !== bImplemented) {
      return aImplemented ? -1 : 1;
    }

    return 0;
  });
};

const useRankedFactions = (availableFactionIds, isRanked, unlockedFactionIds, localMode) => {
  // Порядок сортировки (доступные — вперед) замораживается на момент
  // открытия экрана: unlockedFactionIds меняется, стоит купить фракцию прямо
  // в карусели, а sortByAvailability пересортировала бы весь список — тот же
  // числовой индекс слайда после этого указывал бы на другую фракцию, и
  // карусель визуально "прыгала" бы с только что купленной. Доступность
  // конкретной фракции (кнопка/маска) при этом остается живой — она
  // считается отдельно через checkIfFactionAvailable
  const [frozenUnlockedFactionIds] = useState(() => unlockedFactionIds);

  return useMemo(() => {
    const isFactionAvailableForSort = (faction) => {
      if (isRanked) return true;
      if (!faction.implemented) return false;
      // Локальная игра: то же владение, что и в сетевой; фолбэк «все
      // реализованные» — только при полном отсутствии данных о владении
      if (localMode && frozenUnlockedFactionIds.size === 0) return true;
      return frozenUnlockedFactionIds.has(faction.id);
    };

    const allFactions = sortByAvailability(FACTION_CONFIGS, isFactionAvailableForSort);

    const selectableFactions = isRanked ?
      availableFactionIds.map((id) => allFactions.find((f) => f.id === id)).filter(Boolean)
      : allFactions;

    // «Случайная фракция» — всегда 0 позиция списка (во всех режимах)
    const factions = [RANDOM_FACTION_CONFIG, ...selectableFactions];

    const slides = Array.from(Array(factions.length).keys());

    return [factions, slides];
  }, [availableFactionIds, isRanked, frozenUnlockedFactionIds, localMode]);
};

/**
 * Список фракций для выбора и правила их доступности — общие для экрана
 * выбора фракции (FactionSelectPage: лобби на телефоне, локальная игра) и
 * встроенного выбора в десктопном лобби (LobbyFactionPicker): состав, порядок
 * и то, что можно выбрать, должны совпадать.
 *
 * Порядок: «Случайная фракция» первой, затем доступные, затем закрытые
 * реализованные, затем нереализованные. В рейтинговом лобби — только выданные
 * игроку (availableFactionIds), все доступны, кроме забаненной.
 *
 * @param {Object} params
 * @param {Object} params.auth - private.auth
 * @param {string[]} [params.availableFactionIds] - выданные фракции рейтингового лобби
 * @param {boolean} [params.isRanked]
 * @param {string} [params.bannedFactionId] - итог фазы банов
 * @param {boolean} [params.localMode] - локальная игра (фолбэк владения)
 */
export const useFactionSelectList = ({auth, availableFactionIds, isRanked, bannedFactionId, localMode}) => {
  const unlockedFactionIds = useMemo(() => {
    return new Set(
      (auth.availableFactions || []).filter((f) => f.isAvailable).map((f) => f.id)
    );
  }, [auth.availableFactions]);

  const [factions, slides] = useRankedFactions(availableFactionIds, isRanked, unlockedFactionIds, localMode);

  const factionItems = useMemo(() => {
    return factions.map((faction) => {
      // Моковая «Случайная фракция»: скинов нет — изображение и цвета из конфига
      if (faction.id === RANDOM_FACTION_ID) {
        return {...faction};
      }
      const selectedSkinId = auth.factionLoadouts?.[faction.id]?.selectedSkin;
      const displayProps = getFactionSkinDisplayProps(faction, selectedSkinId);
      return {
        ...faction,
        ...displayProps,
        skinId: selectedSkinId,
      };
    });
  }, [factions, auth.factionLoadouts]);

  const finalAvailableFactionIds = useMemo(() => {
    return factions
      .filter((faction) => {
        if (isRanked) return true;
        if (!faction.implemented) return false;
        // Локальная игра ограничена владением так же, как обычная сетевая;
        // фолбэк на «все реализованные» — только когда данных о владении нет
        // вовсе (гость/оффлайн, auth.availableFactions пуст)
        if (localMode && unlockedFactionIds.size === 0) return true;
        return unlockedFactionIds.has(faction.id);
      })
      .map(faction => faction.id)
  }, [factions, isRanked, unlockedFactionIds, localMode]);

  // Забаненная в рейтинговом лобби фракция (см. utils/lobby/factionBans.js)
  // остается в списке, но выбрать ее нельзя. Сравнение строго по id: при
  // малом числе слайдов карусель задваивает массив, и по индексу фракцию не
  // опознать. «Случайная фракция» забаненной не бывает — ее сервер раскроет в
  // незабаненную
  const checkIfFactionBanned = (faction) => Boolean(bannedFactionId) && faction.id === bannedFactionId;

  const checkIfFactionAvailable = (faction) =>
    !checkIfFactionBanned(faction)
    && (faction.id === RANDOM_FACTION_ID || finalAvailableFactionIds.includes(faction.id));

  // "Подробно" открывает FactionCardOverlay, который ищет реальный конфиг
  // фракции (getFactionConfig) — у моковой "Случайной фракции" его нет, и
  // раньше клик по кнопке крашил приложение, для нее кнопка недоступна.
  // Доступность НЕ привязана к открытости фракции (checkIfFactionAvailable) —
  // детали можно посмотреть и у ещё не купленной, но уже реализованной
  // фракции; недоступна только для нереализованных (implemented: false)
  const checkIfDetailsAvailable = (faction) =>
    faction.id !== RANDOM_FACTION_ID && faction.implemented;

  return {
    factions,
    slides,
    factionItems,
    checkIfFactionBanned,
    checkIfFactionAvailable,
    checkIfDetailsAvailable,
  };
};
